import { useState, useEffect } from "react";
import { Box, Paper, Title, Text, TextInput, PasswordInput, Button, Flex, Badge } from "@mantine/core";
import { IconMail, IconLock, IconShieldCheck } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import {
  TWO_FACTOR_DEVICE_TOKEN_KEY,
  TWO_FACTOR_RETURN_PATH_KEY,
  TWO_FACTOR_TEMP_TOKEN_KEY,
} from "@/two-factor/constants";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { successNotification } from "@/shared/utils/successNotification";
import { errorNotification } from "@/shared/utils/errorNotification";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";

type DecodedToken = {
  id: string;
  email: string;
  role: "ADMIN" | "CLIENT";
  exp: number;
  iat: number;
};

export default function AdminLoginPage() {
  const navigate = useNavigate();

  const existingToken = Cookies.get("token");
  const [redirecting] = useState(() => {
    if (!existingToken) return false;
    try {
      const decoded = jwtDecode<DecodedToken>(existingToken);
      return decoded.exp > Date.now() / 1000;
    } catch {
      Cookies.remove("token");
      return false;
    }
  });

  useEffect(() => {
    if (!redirecting || !existingToken) return;
    try {
      const decoded = jwtDecode<DecodedToken>(existingToken);
      if (decoded.role === "ADMIN") {
        navigate("/admin/companies", { replace: true });
      } else {
        navigate("/profile", { replace: true });
      }
    } catch {
      Cookies.remove("token");
    }
  }, []);

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isPasswordValid = password.length >= 8;
  const isFormValid = email.trim() !== "" && password.trim() !== "" && isValidEmail(email) && isPasswordValid;

  const handleLogin = async () => {
    if (!isFormValid) return;

    setLoading(true);

    try {
      const deviceTok = localStorage.getItem(TWO_FACTOR_DEVICE_TOKEN_KEY);
      const data = await trpc.auth.login.mutate({
        email,
        password,
        ...(deviceTok ? { trustedDeviceToken: deviceTok } : {}),
      });

      if ("requires2FA" in data && data.requires2FA) {
        sessionStorage.setItem(TWO_FACTOR_TEMP_TOKEN_KEY, data.tempToken);
        sessionStorage.setItem(TWO_FACTOR_RETURN_PATH_KEY, "/admin/companies");
        navigate("/login/2fa", { replace: true });
        return;
      }

      if (!("token" in data) || !data.token) {
        errorNotification({
          title: "Login Failed",
          message: "No token received from server.",
        });
        return;
      }

      try {
        const decoded = jwtDecode<DecodedToken>(data.token);

        if (decoded.role !== "ADMIN") {
          errorNotification({
            title: "Access Denied",
            message: "You do not have admin privileges to access this portal.",
          });
          return;
        }

        const expiresInDays = (decoded.exp - Date.now() / 1000) / (60 * 60 * 24);
        Cookies.set("token", data.token, { expires: expiresInDays > 0 ? expiresInDays : 1 });

        successNotification({
          title: "Login Successful!",
          message: "Welcome to the admin portal.",
        });

        navigate("/admin/companies", { replace: true });
      } catch (decodeError) {
        errorNotification({
          title: "Login Failed",
          message: "Invalid token received from server.",
        });
      }
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && isFormValid && !loading) {
      handleLogin();
    }
  };

  if (redirecting) return null;

  return (
    <Box
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--mantine-color-slate-9)",
      }}
    >
      <Flex direction="column" align="center" gap="md">
        <Flex direction="column" align="center" gap="xs" mb="sm">
          <Flex align="center" gap="sm">
            <Title order={1} fz={28} fw={600} c="white">
              GTsetu
            </Title>
            <Badge color="red" size="lg" variant="filled">
              Admin
            </Badge>
          </Flex>
          <Text fz="md" c="slate.4" fw={500}>
            Administration Portal
          </Text>
        </Flex>

        <Paper shadow="md" p="xl" radius="lg" style={{ width: 400 }}>
          <Flex align="center" gap="sm" mb="md">
            <IconShieldCheck size={28} color="var(--mantine-color-primary-6)" />
            <Title order={2} fz="xl">
              Admin Login
            </Title>
          </Flex>
          <Text size="sm" c="slate.6" mb="lg">
            Enter your credentials to access the admin portal.
          </Text>

          <TextInput
            label="Email address"
            placeholder="admin@gtconnect.com"
            required
            mb="md"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            leftSection={<IconMail size={18} color="#6B7280" />}
            error={email && !isValidEmail(email) ? "Please enter a valid email address" : null}
          />

          <PasswordInput
            label="Password"
            placeholder="Your password"
            required
            mb="xl"
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            leftSection={<IconLock size={18} color="#6B7280" />}
            error={password && !isPasswordValid ? "Password must be at least 8 characters" : null}
          />

          <Button fullWidth onClick={handleLogin} loading={loading} disabled={!isFormValid || loading}>
            Login to Admin Portal
          </Button>
        </Paper>
      </Flex>
    </Box>
  );
}
